'use client'

import { useState, useEffect } from 'react'
import { User, Property, Application } from '@/types'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { getApiUrl } from '@/lib/apiUrl'



interface Dispute {
  id: string
  reporter_id: string
  reported_user_id: string
  dispute_type: 'payment' | 'property' | 'behavior' | 'fraud' | 'other'
  title: string
  description: string
  evidence?: string
  status: 'open' | 'under_review' | 'resolved' | 'closed'
  resolution?: string
  admin_notes?: string
  created_at: string
  updated_at: string
  reporter?: User
  reported_user?: User
  property?: Property
  application?: Application
}

interface DisputeResolutionProps {
  onUpdate: () => void
}

export default function DisputeResolution({ onUpdate }: DisputeResolutionProps) {
  const { token } = useUser()
  const toast = useToast()
  const [disputes, setDisputes] = useState<Dispute[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null)
  const [showResolutionModal, setShowResolutionModal] = useState(false)
  const [filter, setFilter] = useState<'all' | 'open' | 'under_review' | 'resolved' | 'closed'>('all')
  const [resolutionText, setResolutionText] = useState('')
  const [adminNotes, setAdminNotes] = useState('')

  // Load data
  const loadData = async () => {
    setLoading(true)
    try {
      if (!token) {
        setDisputes([])
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/disputes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to load disputes')
      }

      setDisputes(payload?.disputes || [])
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load disputes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Update dispute status
  const updateDisputeStatus = async (_disputeId: string, status: string, resolution?: string, notes?: string) => {
    try {
      if (!token) {
        toast.error('Please log in as an admin')
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/disputes/${_disputeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status,
          resolution,
          admin_notes: notes
        })
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to update dispute')
      }

      setShowResolutionModal(false)
      setResolutionText('')
      setAdminNotes('')
      await loadData()
      onUpdate()
      toast.success('Dispute updated successfully')
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to update dispute')
    }
  }

  // Get dispute type color
  const getDisputeTypeColor = (type: string) => {
    switch (type) {
      case 'payment':
        return 'bg-red-100 text-red-800'
      case 'property':
        return 'bg-blue-100 text-blue-800'
      case 'behavior':
        return 'bg-yellow-100 text-yellow-800'
      case 'fraud':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-800'
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800'
      case 'resolved':
        return 'bg-green-100 text-green-800'
      case 'closed':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Filter disputes
  const filteredDisputes = disputes.filter(dispute => {
    if (filter === 'all') return true
    return dispute.status === filter
  })

  // Calculate stats
  const openDisputes = disputes.filter(d => d.status === 'open').length
  const underReviewDisputes = disputes.filter(d => d.status === 'under_review').length
  const resolvedDisputes = disputes.filter(d => d.status === 'resolved').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Dispute Resolution</h2>
        <div className="flex space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Disputes</option>
            <option value="open">Open</option>
            <option value="under_review">Under Review</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Open Disputes</h3>
          <p className="text-3xl font-bold text-red-600">{openDisputes}</p>
          <p className="text-sm text-gray-500">Require immediate attention</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Under Review</h3>
          <p className="text-3xl font-bold text-yellow-600">{underReviewDisputes}</p>
          <p className="text-sm text-gray-500">Being investigated</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Resolved</h3>
          <p className="text-3xl font-bold text-green-600">{resolvedDisputes}</p>
          <p className="text-sm text-gray-500">Successfully resolved</p>
        </div>
      </div>

      {/* Disputes List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Disputes</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading disputes...</p>
          </div>
        ) : filteredDisputes.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredDisputes.map((dispute) => (
              <div key={dispute.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-gray-900">{dispute.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDisputeTypeColor(dispute.dispute_type)}`}>
                        {dispute.dispute_type.charAt(0).toUpperCase() + dispute.dispute_type.slice(1)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(dispute.status)}`}>
                        {dispute.status.charAt(0).toUpperCase() + dispute.status.slice(1)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{dispute.description}</p>
                    
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>
                        <span className="font-medium">Reporter:</span> {dispute.reporter?.email || 'Unknown'}
                      </p>
                      <p>
                        <span className="font-medium">Reported User:</span> {dispute.reported_user?.email || 'Unknown'}
                      </p>
                      <p>
                        <span className="font-medium">Created:</span> {new Date(dispute.created_at).toLocaleDateString()}
                      </p>
                      {dispute.property && (
                        <p>
                          <span className="font-medium">Property:</span> {dispute.property.title}
                        </p>
                      )}
                    </div>

                    {dispute.evidence && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Evidence:</p>
                        <p className="text-sm text-gray-600">{dispute.evidence}</p>
                      </div>
                    )}

                    {dispute.resolution && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Resolution:</p>
                        <p className="text-sm text-gray-600">{dispute.resolution}</p>
                      </div>
                    )}

                    {dispute.admin_notes && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Admin Notes:</p>
                        <p className="text-sm text-gray-600">{dispute.admin_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {dispute.status === 'open' && (
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedDispute(dispute)
                        setShowResolutionModal(true)
                      }}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Review & Resolve
                    </button>
                    <button
                      onClick={() => updateDisputeStatus(dispute.id, 'under_review')}
                      className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                    >
                      Mark Under Review
                    </button>
                  </div>
                )}
                
                {dispute.status === 'under_review' && (
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedDispute(dispute)
                        setShowResolutionModal(true)
                      }}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                    >
                      Resolve Dispute
                    </button>
                    <button
                      onClick={() => updateDisputeStatus(dispute.id, 'open')}
                      className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                    >
                      Reopen
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No disputes found</p>
            <p className="text-sm">All disputes have been resolved</p>
          </div>
        )}
      </div>

      {/* Resolution Modal */}
      {showResolutionModal && selectedDispute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Resolve Dispute</h3>
                <button
                  onClick={() => setShowResolutionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Dispute Details</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium">{selectedDispute.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{selectedDispute.description}</p>
                    <div className="mt-2 text-sm text-gray-500">
                      <p>Reporter: {selectedDispute.reporter?.email}</p>
                      <p>Reported User: {selectedDispute.reported_user?.email}</p>
                      <p>Type: {selectedDispute.dispute_type}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resolution *
                  </label>
                  <textarea
                    value={resolutionText}
                    onChange={(e) => setResolutionText(e.target.value)}
                    placeholder="Describe the resolution and any actions taken..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Internal notes for admin reference..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => {
                      if (resolutionText.trim()) {
                        updateDisputeStatus(
                          selectedDispute.id, 
                          'resolved', 
                          resolutionText, 
                          adminNotes
                        )
                      } else {
                        alert('Please provide a resolution')
                      }
                    }}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Resolve Dispute
                  </button>
                  <button
                    onClick={() => updateDisputeStatus(selectedDispute.id, 'closed')}
                    className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Close Without Resolution
                  </button>
                  <button
                    onClick={() => setShowResolutionModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 



